import React, { PropsWithChildren, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { PagePanel } from 'edenred-ui';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { BenefitGroupForm } from '@epi-forms/BenefitGroup/BenefitGroupForm';
import {
  IBenefitDefaultSettings,
  ICreateBenefitGroupForm,
  IBenefitGroupDetails,
  IBenefits
} from '@epi-models/pages/BenefitGroups';
import { goToPage } from '@epi-actions/navigation';
import { routerPaths } from '@epi-constants/routerPaths';
import { useUpdateBenefitGroupMutation } from '@epi-api/benefitGroups';
import { showNotification } from '@epi-actions/overlays';
import {
  formatValuesToDTO,
  mapBenefitsToSettings
} from '@epi-helpers/BenefitGroupHelper';
import { LunchFundsLoadingValues } from '@epi-models/pages/Home';
import { ApiError } from '@epi-helpers/ApiError';
import { ERROR_RESPONSE_NOT_UNIQUE_NAME } from '@epi-constants/benefitGroups';

type OwnProps = {
  defaultSettings: IBenefitDefaultSettings;
  groupDetails: IBenefitGroupDetails;
  groupId: string;
};

const createDefaultValuesFromSettings = (
  defaultSettings: IBenefitDefaultSettings,
  groupDetails: IBenefitGroupDetails
): ICreateBenefitGroupForm => {
  return {
    groupName: groupDetails.groupName,
    benefitSettings: mapBenefitsToSettings(
      defaultSettings,
      groupDetails
    ) as Record<
      IBenefits,
      {
        maxAmountPerPeriod: number;
        isMaximumSpendingLimit: boolean;
        fundsLoadingType: LunchFundsLoadingValues;
        recurringTopupRequestEnabled: boolean;
      }
    >
  } as ICreateBenefitGroupForm;
};

export const EditBenefitGroupForm = ({
  defaultSettings,
  groupDetails,
  groupId,
  children
}: PropsWithChildren<OwnProps>) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [updateBenefitGroup, { isLoading }] = useUpdateBenefitGroupMutation();

  const defaultValues = useMemo(
    () => createDefaultValuesFromSettings(defaultSettings, groupDetails),
    [defaultSettings, groupDetails]
  );

  const editBenefitGroupForm = useForm<ICreateBenefitGroupForm>({
    mode: 'onChange',
    defaultValues
  });

  const checkedBenefits = editBenefitGroupForm.watch('benefitSettings');

  const isAnyBenefitChecked = checkedBenefits
    ? Object.values(checkedBenefits).some(benefit => !!benefit)
    : false;

  return (
    <FormProvider {...editBenefitGroupForm}>
      <BenefitGroupForm
        handleSubmitForm={async formValues => {
          const payload = formatValuesToDTO(formValues);
          try {
            await updateBenefitGroup({ groupId, payload }).unwrap();
            editBenefitGroupForm.reset(defaultValues);
            dispatch(
              showNotification(
                { message: 'messages.successful_update_benefit_group' },
                'success'
              )
            );
            dispatch(goToPage(routerPaths.benefitGroups));
          } catch (error) {
            if (
              error instanceof ApiError &&
              error.prop?.errors[0]?.code === ERROR_RESPONSE_NOT_UNIQUE_NAME
            ) {
              editBenefitGroupForm.setError('groupName', {
                type: 'server',
                message: t('messages.benefit_group_already_exist')
              });
            } else {
              dispatch(
                showNotification({ message: 'messages.error' }, 'error')
              );
            }
          }
        }}
      >
        <PagePanel
          hasOverflow={false}
          title={t('containers.benefit_groups.edit_group_title')}
          footer={{
            submitText: t('containers.benefit_groups.buttons.save'),
            disableSubmit:
              !editBenefitGroupForm.formState.isValid ||
              !isAnyBenefitChecked ||
              !editBenefitGroupForm.formState.isDirty ||
              isLoading,
            backText: t('containers.benefit_groups.buttons.cancel'),
            onBack: () => {
              dispatch(goToPage(routerPaths.benefitGroups));
            }
          }}
        >
          {children}
        </PagePanel>
      </BenefitGroupForm>
    </FormProvider>
  );
};
